import { AutoPauseController } from './autoPauseController';
import { buildSegment } from './segment';
import { assertStateInvariants, createInitialState } from './shadowingState';
import type { PlayerAdapter, ShadowingSegment, ShadowingState, SubtitleCue } from './types';
import { isTimeWithinRange } from '../utils/time';
import { logger } from '../utils/logger';

export type StateListener = (state: Readonly<ShadowingState>) => void;

export class ShadowingController {
  private cues: SubtitleCue[] = [];
  private state: ShadowingState;
  private allowNativeCueSync = true;
  private readonly listeners = new Set<StateListener>();
  private readonly autoPause: AutoPauseController;

  constructor(
    private readonly player: PlayerAdapter,
    videoId: string | null = null,
  ) {
    this.state = createInitialState(videoId);
    this.autoPause = new AutoPauseController(player, () => {
      this.state.isPlayingSegment = false;
      this.state.autoPauseTarget = null;
      this.emit();
    });
  }

  load(cues: SubtitleCue[], videoId = this.state.videoId): void {
    this.autoPause.clearTarget();
    this.cues = cues;
    this.state = createInitialState(videoId);
    this.allowNativeCueSync = true;
    if (cues.length > 0) {
      this.setSingleCue(this.findCueAtTime(this.player.getCurrentTime()) ?? 0);
    }
    this.emit();
  }

  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => this.listeners.delete(listener);
  }

  getState(): Readonly<ShadowingState> {
    return { ...this.state };
  }

  getCues(): readonly SubtitleCue[] {
    return this.cues;
  }

  getSegment(): ShadowingSegment | null {
    const { segmentStartCueIndex: start, segmentEndCueIndex: end } = this.state;
    return start === null || end === null ? null : buildSegment(this.cues, start, end);
  }

  selectCue(index: number): boolean {
    if (!this.isValidIndex(index)) return false;
    this.allowNativeCueSync = false;
    this.setSingleCue(index);
    this.emit();
    return this.replay();
  }

  selectThrough(index: number): boolean {
    const start = this.state.segmentStartCueIndex;
    if (start === null || !this.isValidIndex(index) || index < start) return false;
    this.allowNativeCueSync = false;
    this.state.segmentEndCueIndex = index;
    this.emit();
    return true;
  }

  replay(): boolean {
    const segment = this.getSegment();
    if (!segment) return false;
    this.allowNativeCueSync = false;
    this.autoPause.clearTarget();
    this.player.seek(segment.startTime);
    this.autoPause.setTarget(segment.endTime);
    this.state.isPlayingSegment = true;
    this.state.autoPauseTarget = segment.endTime;
    this.emit();
    try {
      const playResult = this.player.play();
      if (playResult instanceof Promise) {
        void playResult.catch((error: unknown) => {
          logger.warn('Playback was blocked', error);
          this.stopSegmentPlayback();
        });
      }
    } catch (error) {
      logger.warn('Unable to play segment', error);
      this.stopSegmentPlayback();
    }
    return true;
  }

  next(): boolean {
    const end = this.state.segmentEndCueIndex;
    if (end === null || end + 1 >= this.cues.length) return false;
    this.allowNativeCueSync = false;
    this.setSingleCue(end + 1);
    this.emit();
    return this.replay();
  }

  previous(): boolean {
    const start = this.state.segmentStartCueIndex;
    if (start === null || start === 0) return false;
    this.allowNativeCueSync = false;
    this.setSingleCue(start - 1);
    this.emit();
    return this.replay();
  }

  extend(): boolean {
    const end = this.state.segmentEndCueIndex;
    if (end === null || end + 1 >= this.cues.length) return false;
    this.allowNativeCueSync = false;
    this.state.segmentEndCueIndex = end + 1;
    this.emit();
    return true;
  }

  shrink(): boolean {
    const { segmentStartCueIndex: start, segmentEndCueIndex: end } = this.state;
    if (start === null || end === null || start === end) return false;
    this.allowNativeCueSync = false;
    this.state.segmentEndCueIndex = end - 1;
    this.emit();
    return true;
  }

  cancelMultiSelection(): boolean {
    const { segmentStartCueIndex: start, segmentEndCueIndex: end } = this.state;
    if (start === null || end === null || start === end) return false;
    this.allowNativeCueSync = false;
    this.state.segmentEndCueIndex = start;
    this.emit();
    return true;
  }

  togglePlay(): boolean {
    if (this.cues.length === 0) return false;
    if (!this.player.isPaused()) {
      this.allowNativeCueSync = false;
      this.player.pause();
      this.stopSegmentPlayback();
      return true;
    }

    const segment = this.getSegment();
    const currentTime = this.player.getCurrentTime();
    if (segment && isTimeWithinRange(currentTime, segment.startTime, segment.endTime)) {
      this.allowNativeCueSync = false;
      this.autoPause.setTarget(segment.endTime);
      this.state.isPlayingSegment = true;
      this.state.autoPauseTarget = segment.endTime;
    } else {
      this.allowNativeCueSync = true;
      this.stopSegmentPlayback();
    }
    this.emit();
    void this.player.play();
    return true;
  }

  handleSeeking(): void {
    const segment = this.getSegment();
    if (!segment || !isTimeWithinRange(this.player.getCurrentTime(), segment.startTime, segment.endTime)) {
      this.stopSegmentPlayback();
      this.allowNativeCueSync = true;
    }
  }

  syncCurrentCue(time: number): void {
    if (
      !this.allowNativeCueSync ||
      this.state.isPlayingSegment ||
      this.state.autoPauseTarget !== null
    ) return;
    const index = this.findCueAtTime(time);
    if (index === null || index === this.state.currentCueIndex) return;
    this.setSingleCue(index);
    this.emit();
  }

  setEnabled(enabled: boolean): void {
    if (!enabled) this.stopSegmentPlayback();
  }

  handlePlayerPaused(): void {
    if (this.state.autoPauseTarget === null) return;
    this.autoPause.stop();
    this.state.isPlayingSegment = false;
    this.emit();
  }

  handlePlayerPlaying(): void {
    const segment = this.getSegment();
    if (this.state.autoPauseTarget === null) return;
    if (
      !segment ||
      !isTimeWithinRange(this.player.getCurrentTime(), segment.startTime, segment.endTime)
    ) return;
    this.autoPause.start();
    this.state.isPlayingSegment = true;
    this.emit();
  }

  dispose(): void {
    this.autoPause.dispose();
    this.listeners.clear();
  }

  private setSingleCue(index: number): void {
    this.state.currentCueIndex = index;
    this.state.segmentStartCueIndex = index;
    this.state.segmentEndCueIndex = index;
  }

  private stopSegmentPlayback(): void {
    this.autoPause.clearTarget();
    this.state.isPlayingSegment = false;
    this.state.autoPauseTarget = null;
    this.emit();
  }

  private findCueAtTime(time: number): number | null {
    let candidate: number | null = null;
    for (let index = 0; index < this.cues.length; index += 1) {
      const cue = this.cues[index];
      if (time >= cue.start && time <= cue.end) return index;
      if (cue.start <= time) candidate = index;
      if (cue.start > time) break;
    }
    return candidate;
  }

  private isValidIndex(index: number): boolean {
    return Number.isInteger(index) && index >= 0 && index < this.cues.length;
  }

  private emit(): void {
    assertStateInvariants(this.state, this.cues.length);
    const snapshot = this.getState();
    this.listeners.forEach((listener) => listener(snapshot));
  }
}
